import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col,
    Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Badge
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost, mediaUrl } from "../../Utils";
import { useAuth } from "../../Context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useConfig } from "../../Context/ConfigContext";
import { H3, P } from "../../AbstractElements";
import {
    CheckCircle, Calendar, DollarSign, User, Eye,
    RefreshCcw, Info, ArrowRight, CreditCard, Clock
} from 'react-feather';

interface PayoutSummary {
    bystander_id: number;
    payout_status: 'none' | 'pending' | 'paid';
    booking_count: number;
    total_amount: string;
    last_request_at: string;
    last_payout_at: string | null;
    bystander: {
        id: number;
        name: string;
        profile_pic: string | null;
    };
}

interface BookingDetail {
    id: number;
    patient_name: string;
    date: string;
    slot_start_time: string;
    bystander_total_amount: string;
    user: {
        name: string;
    };
}

export default function PayoutManagement() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canPay = hasPermission('payout-management.pay');
    const [data, setData] = useState<PayoutSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState("all");

    // Approval Modal
    const [approveModal, setApproveModal] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState<PayoutSummary | null>(null);
    const [payoutRef, setPayoutRef] = useState("");
    const [approving, setApproving] = useState(false);
    const { config } = useConfig();

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedStatus]);

    useEffect(() => {
        fetchData(currentPage, perPage);
    }, [selectedStatus, currentPage, perPage]);

    const fetchData = async (page: number, size: number) => {
        setLoading(true);
        try {
            const response: any = await apiGet(`payouts/summary?status=${selectedStatus}&page=${page}&per_page=${size}`);
            setData(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            toast.error("Failed to load payout summary");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const toggleApproveModal = (payout?: PayoutSummary) => {
        if (payout) {
            setSelectedPayout(payout);
            setPayoutRef("");
        }
        setApproveModal(!approveModal);
    };

    const handleApprove = async () => {
        if (!selectedPayout) return;

        setApproving(true);
        try {
            const response: any = await apiPost("payouts/approve-batch", {
                bystander_id: selectedPayout.bystander_id,
                payout_ref: payoutRef
            });
            if (response && (response.status === true || response.updated_count !== undefined)) {
                toast.success(response.message || "Payout marked as paid successfully");
                setApproveModal(false);
                setSelectedPayout(null);
                setPayoutRef("");
                fetchData(currentPage, perPage);
            } else {
                toast.error(response?.message || "Approval failed");
            }
        } catch (error) {
            toast.error("Failed to approve payout");
        } finally {
            setApproving(false);
        }
    };

    const columns = [
        {
            name: <span className="text-muted small text-uppercase fw-bold">SL.NO</span>,
            selector: (row: any, index: any) => index + 1,
            width: '70px',
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Bystander</span>,
            cell: (row: PayoutSummary) => (
                <div className="d-flex align-items-center gap-3 py-2">
                    <div className="avatar-wrapper shadow-sm" style={{
                        width: '45px', height: '45px', borderRadius: '12px',
                        backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'center', alignItems: 'center',
                        overflow: 'hidden', border: '1px solid #e2e8f0'
                    }}>
                        {row.bystander.profile_pic ? (
                            <img src={`${mediaUrl}${row.bystander.profile_pic}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <User size={20} className="text-muted" />
                        )}
                    </div>
                    <div>
                        <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>
                            {row.bystander.name} <span className="text-muted small fw-normal">(#{row.bystander_id})</span>
                        </div>
                    </div>
                </div>
            ),
            sortable: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold text-center w-100">Unit Volume</span>,
            cell: (row: PayoutSummary) => (
                <div className="text-center w-100">
                    <div className="fw-bold text-dark">{row.booking_count}</div>
                    <small className="text-muted">Total Bookings</small>
                </div>
            ),
            sortable: true,
            center: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold text-center w-100">Batch Value</span>,
            cell: (row: PayoutSummary) => (
                <div className="text-center w-100">
                    <div className="fw-bold text-success" style={{ fontSize: '1.1rem' }}>
                        {config.currency}{parseFloat(row.total_amount).toLocaleString()}
                    </div>
                    {row.payout_status === 'pending' && <small className="text-warning fw-bold">PENDING APPROVAL</small>}
                    {row.payout_status === 'none' && <small className="text-muted fw-bold">NOT YET REQUESTED</small>}
                </div>
            ),
            sortable: true,
            center: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Requested On</span>,
            cell: (row: PayoutSummary) => (
                <div className="py-2">
                    <div className="d-flex align-items-center gap-2 small fw-bold text-dark">
                        <Clock size={12} className="text-muted" />
                        {row.payout_status === 'none' ? 'Last Active:' : (row.payout_status === 'pending' ? 'Requested:' : 'Paid on:')}
                    </div>
                    <div className="small text-muted mt-1">
                        {row.payout_status === 'none' 
                            ? (row.last_request_at ? new Date(row.last_request_at).toLocaleDateString('en-GB') : 'N/A')
                            : new Date(row.payout_status === 'pending' ? row.last_request_at : row.last_payout_at!).toLocaleDateString('en-GB')
                        }
                    </div>
                </div>
            ),
            sortable: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold text-center w-100">Status</span>,
            center: true,
            cell: (row: PayoutSummary) => (
                <Badge 
                    color={row.payout_status === 'paid' ? 'success' : (row.payout_status === 'pending' ? 'warning' : 'secondary')} 
                    pill className="px-3 py-2 text-uppercase" 
                    style={{ fontSize: '10px', fontWeight: '800' }}
                >
                    {row.payout_status === 'paid' ? 'Paid' : (row.payout_status === 'pending' ? 'Pending' : 'Accrued')}
                </Badge>
            ),
            sortable: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold text-center w-100">Actions</span>,
            center: true,
            width: "220px",
            cell: (row: PayoutSummary) => (
                <div className="d-flex gap-2 justify-content-center align-items-center w-100">
                    <Button
                        size="sm"
                        color="primary"
                        outline
                        className="btn-pill d-flex align-items-center gap-1 shadow-sm px-3"
                        style={{ fontSize: '12px', height: '32px' }}
                        onClick={() => navigate(`${process.env.PUBLIC_URL}/payout_details`, {
                            state: {
                                bystander_id: row.bystander_id,
                                bystander_name: row.bystander.name,
                                status: row.payout_status
                            }
                        })}
                    >
                        <Eye size={14} /> Details
                    </Button>
                    {row.payout_status === 'pending' && canPay && (
                        <Button
                            size="sm"
                            color="success"
                            className="btn-pill d-flex align-items-center gap-1 shadow-sm px-3"
                            style={{ fontSize: '12px', height: '32px' }}
                            onClick={() => toggleApproveModal(row)}
                        >
                            <CheckCircle size={14} /> Pay
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '75px' },
        },
    };

    return (
        <div className='page-body' style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
            <Breadcrumbs pageTitle="Financial Operations" parent="Management" title="Payout Ledger" />

            <Container fluid className="px-4">
                <Card className="border-0 shadow-lg" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                    <div style={{
                        background: 'linear-gradient(90deg, #064e3b 0%, #10b981 100%)',
                        padding: '1.5rem 2rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <h2 className="text-white mb-0 fw-bold">Liquidity & Payouts</h2>
                            <p className="text-white opacity-75 mb-0 small">Audit and authorize bystander earnings distribution</p>
                        </div>
                    </div>

                    <CardBody className="bg-white p-4">
                        <Row className="mb-4 align-items-center g-0 border-bottom">
                            <Col md={12}>
                                <div className="d-flex custom-tabs" style={{ gap: '30px' }}>
                                    {[
                                        { id: 'all', label: 'Consolidated Stream' },
                                        { id: 'pending', label: 'Unpaid Requests' },
                                        { id: 'paid', label: 'Payout History' },
                                        { id: 'none', label: 'Accrued (Unrequested)' },
                                    ].map((status) => (
                                        <div
                                            key={status.id}
                                            className={`tab-item ${selectedStatus === status.id ? 'active-tab' : ''}`}
                                            onClick={() => setSelectedStatus(status.id)}
                                            style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: selectedStatus === status.id ? '#064e3b' : '#64748b',
                                                cursor: 'pointer',
                                                padding: '12px 2px',
                                                position: 'relative'
                                            }}
                                        >
                                            {status.label}
                                            {selectedStatus === status.id && (
                                                <div style={{ position: 'absolute', bottom: '-1px', left: 0, width: '100%', height: '3px', background: '#10b981', borderRadius: '10px 10px 0 0' }} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        <div className="rounded border shadow-sm overflow-hidden">
                            <DataTable
                                columns={columns}
                                data={data}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                highlightOnHover
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                                customStyles={customStyles}
                                noDataComponent={
                                    <div className="p-5 text-center">
                                        <div className="mb-3 text-muted opacity-25">
                                            <Info size={64} />
                                        </div>
                                        <h4 className="fw-bold text-dark">Clear Balance</h4>
                                        <p className="text-muted">No pending or historical payouts found in this category.</p>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>

            {/* Approval Modal */}
            <Modal isOpen={approveModal} toggle={() => setApproveModal(false)} centered>
                <div style={{ background: '#10b981', padding: '1.5rem', color: 'white', borderRadius: '15px 15px 0 0' }}>
                    <ModalHeader toggle={() => setApproveModal(false)} className="border-0 p-0 text-white">
                        <CreditCard size={20} className="me-2" /> Authorize Disbursement
                    </ModalHeader>
                </div>
                <ModalBody className="p-4">
                    {selectedPayout && (
                        <div className="text-center mb-4 p-4 bg-light rounded-lg border border-dashed">
                            <div className="mb-2 opacity-75 small text-uppercase fw-bold text-muted">Amount to Remit</div>
                            <h1 className="fw-bold text-success mb-2">{config.currency}{parseFloat(selectedPayout.total_amount).toLocaleString()}</h1>
                            <p className="text-muted small mb-0"> Marking <span className="fw-bold text-dark">{selectedPayout.booking_count} bookings</span> as settled for <span className="fw-bold text-dark">{selectedPayout.bystander.name} (#{selectedPayout.bystander_id})</span>.</p>
                        </div>
                    )}
                    <Form>
                        <FormGroup>
                            <Label className="fw-bold small text-muted text-uppercase">Transaction Identifier</Label>
                            <Input
                                type="text"
                                placeholder="UTR Number, UPI Ref, or Date Code"
                                value={payoutRef}
                                onChange={(e) => setPayoutRef(e.target.value)}
                                className="form-control-lg border-2"
                            />
                            <small className="text-muted mt-2 d-block">This reference helps bystanders track cross-bank settlements.</small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter className="border-0 p-4 pt-0">
                    <Button color="outline-secondary" outline className="btn-pill px-4" onClick={() => setApproveModal(false)} disabled={approving}>Cancel</Button>
                    <Button color="success" onClick={handleApprove} disabled={approving || !payoutRef} className="btn-pill px-4 shadow-sm">
                        {approving ? <Spinner size="sm" /> : "Confirm Settlement"}
                    </Button>
                </ModalFooter>
            </Modal>

            <ToastContainer />
            <style>{`
                .btn-pill { border-radius: 50px !important; }
                .tab-item:hover { color: #064e3b !important; }
                .active-tab { color: #064e3b !important; }
            `}</style>
        </div>
    );
}
